// 🔴 ETI-82 — SURFACE ADMIN des LIENS commerciaux (Étincelle).
//
// L'admin émet / liste / copie / révoque le lien de soumission d'un commercial.
// 🔑 L'empreinte sha256 reste l'AUTORITÉ serveur ; ⛔ le front ⛔ ne déchiffre rien
//    (il affiche le lien que le serveur, lui, a le droit de rendre à l'admin).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Ban, RefreshCw, Plus } from "lucide-react";

import { OHGrid } from "../../../components/ui/oh-grid";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../../components/ui/select";
import {
  listerLiens, listerCommerciaux, listerOperations, emettreLien, revoquerLien,
} from "./api";

function formatDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return String(v); }
}

async function copier(lien) {
  try {
    await navigator.clipboard.writeText(lien);
    toast.success("Lien copié dans le presse-papier");
  } catch {
    toast.error("Copie impossible — sélectionnez puis copiez-le manuellement");
  }
}

export default function RegieLiensAdmin({ tenantId }) {
  const [commerciaux, setCommerciaux] = useState([]);
  const [operations, setOperations] = useState([]);
  const [liens, setLiens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ commercial_id: "", operation_code: "" });
  const [emitting, setEmitting] = useState(false);
  // Dernier lien émis/retrouvé : mis en avant pour copie immédiate.
  const [dernierLien, setDernierLien] = useState(null);

  const nomCommercial = useCallback((uid) => {
    const c = commerciaux.find((x) => x.user_id === uid);
    return c ? (c.name || c.email || c.user_id) : uid;
  }, [commerciaux]);

  const libelleOperation = useCallback((code) => {
    const o = operations.find((x) => x.code === code);
    return o ? `${o.code} — ${o.libelle}` : code;
  }, [operations]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rc, ro, rl] = await Promise.all([
        listerCommerciaux(tenantId).catch(() => ({ commerciaux: [] })),
        listerOperations(tenantId).catch(() => ({ operations: [] })),
        listerLiens(tenantId),
      ]);
      setCommerciaux(rc.commerciaux || []);
      setOperations(ro.operations || []);
      setLiens(rl.liens || []);
    } catch (e) {
      toast.error(e.message || "Erreur de chargement des liens");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const emettre = async () => {
    if (!form.commercial_id || !form.operation_code) {
      toast.error("Choisissez un commercial et une opération");
      return;
    }
    setEmitting(true);
    try {
      const res = await emettreLien(tenantId, form.commercial_id, form.operation_code);
      toast[res.cree ? "success" : "info"](
        res.cree ? "Lien émis" : "Un lien actif existait déjà — réaffiché"
      );
      if (res.lien) setDernierLien({ ...res, nom: nomCommercial(form.commercial_id) });
      await load();
    } catch (e) {
      toast.error(e.message || "Émission refusée");
    } finally {
      setEmitting(false);
    }
  };

  const revoquer = async (empreinte) => {
    if (!window.confirm("Révoquer ce lien ? Il deviendra inutilisable (les soumissions déjà faites restent).")) return;
    try {
      await revoquerLien(tenantId, empreinte);
      toast.success("Lien révoqué");
      if (dernierLien && dernierLien.empreinte === empreinte) setDernierLien(null);
      await load();
    } catch (e) {
      toast.error(e.message || "Révocation impossible");
    }
  };

  const rows = useMemo(() => (liens || []).map((l) => ({
    id: l.empreinte,
    commercial: nomCommercial(l.commercial_id),
    operation: libelleOperation(l.operation_code),
    statut: l.actif ? "actif" : "revoque",
    cree_le: l.cree_le,
    lien: l.lien,
    a_regenerer: l.a_regenerer,
    _raw: l,
  })), [liens, nomCommercial, libelleOperation]);

  const columns = useMemo(() => [
    { key: "commercial", label: "Commercial", sortable: true },
    { key: "operation", label: "Opération", sortable: true },
    {
      key: "statut", label: "Statut", sortable: true,
      render: (v) => (
        <Badge style={v === "actif"
          ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }
          : { background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>
          {v === "actif" ? "Actif" : "Révoqué"}
        </Badge>
      ),
    },
    { key: "cree_le", label: "Créé le", sortable: true, render: (v) => formatDate(v) },
    {
      key: "lien", label: "Lien", sortable: false,
      render: (v, row) => {
        if (row.statut !== "actif") return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
        if (row.a_regenerer) return <span style={{ color: "var(--oh-stat-amber-text)" }}>à régénérer</span>;
        if (!v) return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
        return (
          <Button variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => copier(v)} aria-label="Copier le lien" data-testid="eti82-copier-ligne">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copier
          </Button>
        );
      },
    },
  ], []);

  const rowActions = useMemo(() => [
    {
      label: "Copier le lien", icon: Copy,
      show: (row) => row.statut === "actif" && !!row.lien && !row.a_regenerer,
      onClick: (row) => copier(row.lien),
    },
    {
      label: "Régénérer (révoquer + réémettre)", icon: RefreshCw,
      show: (row) => row.statut === "actif" && row.a_regenerer,
      onClick: async (row) => {
        await revoquer(row.id);
        toast.info("Réémettez le lien depuis le formulaire ci-dessus.");
      },
    },
    {
      label: "Révoquer", icon: Ban, className: "text-[var(--oh-error-text)]",
      show: (row) => row.statut === "actif",
      onClick: (row) => revoquer(row.id),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [dernierLien, tenantId]);

  const stats = useMemo(() => {
    const actifs = rows.filter((r) => r.statut === "actif").length;
    return [
      { key: "all", label: "Total", value: rows.length, color: "blue", filter: null },
      { key: "actif", label: "Actifs", value: actifs, color: "green", filter: (r) => r.statut === "actif" },
      { key: "revoque", label: "Révoqués", value: rows.length - actifs, color: "red", filter: (r) => r.statut === "revoque" },
    ];
  }, [rows]);

  return (
    <div className="p-4 space-y-4" style={{ color: "var(--oh-text)" }}>
      <div>
        <h1 style={{ fontWeight: 500, fontSize: "1.25rem" }}>Liens commerciaux</h1>
        <p style={{ color: "var(--oh-text-secondary)" }}>
          Émets un lien de soumission par commercial et par opération. Un seul lien actif
          par couple ; révoquer le rend inutilisable sans toucher aux soumissions passées.
        </p>
      </div>

      {/* Émission */}
      <div style={{
        background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
        borderRadius: 12, padding: 16,
      }}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1" style={{ minWidth: 220 }}>
            <label htmlFor="eti82-commercial" style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>Commercial</label>
            <Select value={form.commercial_id} onValueChange={(v) => setForm((f) => ({ ...f, commercial_id: v }))}>
              <SelectTrigger id="eti82-commercial" data-testid="eti82-select-commercial" aria-label="Commercial">
                <SelectValue placeholder="Choisir un commercial" />
              </SelectTrigger>
              <SelectContent>
                {commerciaux.length === 0 && <SelectItem value="__none" disabled>Aucun commercial (rôle régie)</SelectItem>}
                {commerciaux.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email || c.user_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1" style={{ minWidth: 260 }}>
            <label htmlFor="eti82-operation" style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>Opération</label>
            <Select value={form.operation_code} onValueChange={(v) => setForm((f) => ({ ...f, operation_code: v }))}>
              <SelectTrigger id="eti82-operation" data-testid="eti82-select-operation" aria-label="Opération">
                <SelectValue placeholder="Choisir une opération" />
              </SelectTrigger>
              <SelectContent>
                {operations.length === 0 && <SelectItem value="__none" disabled>Aucune opération configurée</SelectItem>}
                {operations.map((o) => (
                  <SelectItem key={o.code} value={o.code}>{o.code} — {o.libelle}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="default" onClick={emettre} disabled={emitting} data-testid="eti82-emettre">
            <Plus className="w-4 h-4 mr-1.5" /> {emitting ? "Émission…" : "Émettre le lien"}
          </Button>
        </div>

        {dernierLien && dernierLien.lien && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" style={{
            background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)",
            borderRadius: 8, padding: "8px 12px",
          }}>
            <span style={{ fontSize: 13 }}>Lien de {dernierLien.nom} :</span>
            <code style={{ fontSize: 12, wordBreak: "break-all" }} data-testid="eti82-dernier-lien">{dernierLien.lien}</code>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => copier(dernierLien.lien)} aria-label="Copier le lien émis">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copier
            </Button>
          </div>
        )}
      </div>

      <OHGrid
        data={rows}
        columns={columns}
        rowActions={rowActions}
        stats={stats}
        loading={loading}
        onRefresh={load}
        defaultSortKey="cree_le"
        defaultSortDir="desc"
        searchPlaceholder="Rechercher un commercial / une opération…"
        emptyMessage="Aucun lien émis pour l'instant"
        testIdPrefix="eti82-liens"
      />
    </div>
  );
}
